import { createContext, memo, useMemo } from 'react'
import type { CSSProperties, PropsWithChildren } from 'react'
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type CrewSortableUnorderedListItemProps = {
  id: UniqueIdentifier
  className?: string
}

type CrewSortableUnorderedListItemContextType = {
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

export const CrewSortableUnorderedListItemContext =
  createContext<CrewSortableUnorderedListItemContextType>({
    attributes: {},
    listeners: undefined,
    ref() {},
  })

// Sortable list item
export const CrewSortableUnorderedListItem = memo(
  ({
    children,
    id,
    className = '',
  }: PropsWithChildren<CrewSortableUnorderedListItemProps>) => {
    const {
      attributes,
      isDragging,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id })

    const context = useMemo(
      () => ({
        attributes,
        listeners,
        ref: setNodeRef, // li要素自体をドラッグ可能にする
      }),
      [attributes, listeners, setNodeRef]
    )

    const style: CSSProperties = {
      opacity: isDragging ? 0.4 : undefined,
      transform: CSS.Transform.toString(transform),
      transition,
    }

    return (
      <CrewSortableUnorderedListItemContext.Provider value={context}>
        <li
          ref={setNodeRef}
          style={style}
          className={`${className} cursor-grab active:cursor-grabbing`}
          {...attributes}
          {...listeners}
        >
          {children}
        </li>
      </CrewSortableUnorderedListItemContext.Provider>
    )
  }
)
